import React, { useEffect, useRef } from 'react'
import { REPORT_CATEGORIES } from '../../helpers/studentReport'
import './StudentReportModal.css'

const StudentReportModal = ({
  reportsEnabled,
  screencapAllowed,
  category,
  message,
  includeScreenshot,
  submitting,
  capturing,
  error,
  success,
  hasParent = true,
  onCategoryChange,
  onMessageChange,
  onScreenshotChange,
  onClose,
  onSubmit
}) => {
  const messageRef = useRef(null)
  const trimmed = message.trim()
  const canSubmit = reportsEnabled
    && !submitting
    && !success
    && Boolean(category)
    && trimmed.length > 0
    && trimmed.length <= 4000

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, submitting])

  useEffect(() => {
    if (reportsEnabled && !success) {
      messageRef.current?.focus()
    }
  }, [reportsEnabled, success])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!canSubmit) return
    onSubmit()
  }

  return (
    <div
      className={`student-report-modal${capturing ? ' student-report-modal--capturing' : ''}`}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose()
      }}
    >
      <div
        className="student-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-report-title"
      >
        <div className="student-report-header">
          <h2 id="student-report-title">Report</h2>
          <button
            type="button"
            className="student-report-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {success ? (
          <div className="student-report-body">
            <p className="student-report-success">Your report was sent.</p>
            <p>
              {hasParent
                ? 'Quillnote will look into it and email your parent with any updates. Quillnote will not email you.'
                : 'Quillnote will look into it. This report goes only to Quillnote. Quillnote will not email you.'}
            </p>
            <div className="student-report-actions">
              <button type="button" className="student-report-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : !reportsEnabled ? (
          <div className="student-report-body">
            <p>
              Your parent has turned off report submissions. Ask your parent to
              turn reporting on in the student Settings tab if you need to send
              a report.
            </p>
            <div className="student-report-actions">
              <button type="button" className="student-report-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <form className="student-report-body" onSubmit={handleSubmit}>
            <fieldset className="student-report-categories" disabled={submitting}>
              <legend>What are you sending?</legend>
              {REPORT_CATEGORIES.map((option) => (
                <label
                  key={option.id}
                  className={`student-report-choice${category === option.id ? ' selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="report-category"
                    value={option.id}
                    checked={category === option.id}
                    onChange={() => onCategoryChange(option.id)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </fieldset>

            <label className="student-report-message-label" htmlFor="student-report-message">
              What should Quillnote know?
            </label>
            <textarea
              id="student-report-message"
              ref={messageRef}
              className="student-report-message"
              value={message}
              onChange={(event) => onMessageChange(event.target.value.slice(0, 4000))}
              maxLength={4000}
              rows={6}
              disabled={submitting}
              placeholder="Describe the bug, the feedback, or the feature you want."
            />
            <div className="student-report-count">{message.length} / 4000</div>

            {screencapAllowed && (
              <label className="student-report-screenshot">
                <span className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={includeScreenshot}
                    onChange={(event) => onScreenshotChange(event.target.checked)}
                    disabled={submitting}
                  />
                  <span className="toggle-slider"></span>
                </span>
                <span className="student-report-screenshot-title">Include screenshot</span>
              </label>
            )}

            {error && <p className="student-report-error" role="alert">{error}</p>}

            <div className="student-report-actions">
              <button
                type="button"
                className="student-report-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="student-report-primary" disabled={!canSubmit}>
                {submitting ? 'Sending...' : 'Send report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default StudentReportModal
